import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  ViewEncapsulation,
  ContentChild,
  ElementRef,
  ViewChild,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  input,
  effect,
  AfterViewInit,
  inject,
} from '@angular/core';
import { AIService } from '../ai.service';
import { ITravelAssistantMessage } from '../../../models/travel.assistant';
import { TravelBuddySettings } from '../../../models/travel.buddy.settings';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ButtonTriggerEvent } from 'src/app/common/components/button/button.component';

@Component({
  selector: 'ig-msg-box',
  templateUrl: './msg.box.component.html',
  styleUrls: ['./msg.box.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class MsgBoxComponent implements OnInit, AfterViewInit {
  defaultAvatar = `https://iti-images.s3.amazonaws.com/events/46a894bb-0ef0-270c-7118-e3bacdad1c9a.webp`;
  private readonly emptyMatchedPlaces = [];
  private readonly approvedMatchedPlacesCache = new WeakMap<any[], any[]>();
  @Input() isAllowClick = false;

  @Input() message: ITravelAssistantMessage;
  @Input() errorMsg: string;
  @Input() isScreenExpanded: boolean = false;
  @Input() travelBuddySettings: TravelBuddySettings;
  @Input() avatar: string;
  @Input() letterAvatar: string | undefined = undefined;
  @Input() loadingTips: string[] = [];
  @Input() canSaveMoreTrips: boolean = true;
  @Output() public ask = new EventEmitter<string>();
  @Output() public tryAgain = new EventEmitter<string>();
  @Output() public avatarClicked = new EventEmitter<any>();
  @Output() public copyResponseEvent = new EventEmitter<any>();
  @Output() public shareResponseEvent = new EventEmitter<any>();
  @Output() public saveForLaterEvent = new EventEmitter<any>();
  @Output() public likeEvent = new EventEmitter<any>();
  @Output() public dislikeEvent = new EventEmitter<any>();
  @Output() public regenerateEvent = new EventEmitter<any>();
  @Output() public feedbackEvent = new EventEmitter<{
    message: ITravelAssistantMessage;
    feedback: 'good' | 'bad';
  }>();
  @Output() public editMessageEvent = new EventEmitter<ITravelAssistantMessage>();
  @Output() public deleteMessageEvent = new EventEmitter<ITravelAssistantMessage>();
  @Output() public requestLoginEvent = new EventEmitter<void>();
  @Output() public saveTripEvent = new EventEmitter<{
    message: ITravelAssistantMessage;
    title: string;
    callback: () => void;
  }>();

  tripTitle: string = '';
  isSavingTrip = false;

  @ViewChild('addingContent', { static: false }) addingContent: ElementRef;
  @ViewChild('saveTripMenuTrigger', { static: false }) saveTripMenuTrigger: MatMenuTrigger;
  @ViewChild('addingContentExpanded', { static: false })
  addingContentExpanded: ElementRef;

  // Cache whether there is additional content to avoid NG0100 caused by function calls in template
  hasAdding = false;
  private addingObserver: MutationObserver | null = null;

  snackBar = inject(MatSnackBar);

  constructor(
    private _aiService: AIService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this._aiService.aiResponseSubject$.subscribe((response) => {
      if (this.message.type === 'AI_STREAM') {
        this.message.msg += response;
      }
    });
  }

  ngAfterViewInit(): void {
    // Initialize cached flag
    this.updateHasAdding();

    // Observe changes to projected content to keep flag in sync without calling functions in template
    if (this.addingContent?.nativeElement) {
      this.addingObserver = new MutationObserver(() => {
        this.updateHasAdding();
      });
      this.addingObserver.observe(this.addingContent.nativeElement, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }
  }

  getAvailableMatchedPlaces(placesMatched) {
    if (!placesMatched?.length) {
      return this.emptyMatchedPlaces;
    }

    const cached = this.approvedMatchedPlacesCache.get(placesMatched);
    if (cached) {
      return cached;
    }

    const result = placesMatched.filter((x) => x.isApproved);
    this.approvedMatchedPlacesCache.set(placesMatched, result);
    return result;
  }

  private updateHasAdding(): void {
    const html = this.addingContent?.nativeElement?.innerHTML as string | undefined;
    if (typeof html === 'string') {
      const trimmed = html.trim();
      const noContent = trimmed.startsWith('<!--') && trimmed.endsWith('-->');
      this.hasAdding = trimmed.length > 0 && !noContent;
    } else {
      this.hasAdding = false;
    }
    // Defer change detection to next tick to avoid ExpressionChanged errors
    setTimeout(() => this.cdr.detectChanges(), 0);
  }

  askQ(question: string) {
    this.ask.emit(question);
  }

  tryAskAgain() {
    this.tryAgain.emit();
  }

  copyResponse() {
    this.copyResponseEvent.emit(this.message);
  }

  shareResponse() {
    this.shareResponseEvent.emit(this.message);
  }

  saveForLater() {
    this.saveForLaterEvent.emit(this.message);
  }

  giveFeedback(feedback: 'good' | 'bad') {
    // Update the message feedback
    this.message.feedback = feedback;

    // Emit the feedback event
    this.feedbackEvent.emit({
      message: this.message,
      feedback: feedback,
    });

    // Trigger change detection to update the UI
    this.cdr.detectChanges();
  }

  likeResponse() {
    this.likeEvent.emit(this.message);
  }

  dislikeResponse() {
    this.dislikeEvent.emit(this.message);
  }

  regenerateResponse() {
    this.regenerateEvent.emit(this.message);
  }

  get getModel() {
    return this.message.model ? `(${this.message.model})` : '';
  }

  hasAddingContent() {
    const html = this.addingContent?.nativeElement.innerHTML;
    if (html) {
      const noContent = html.startsWith('<!-') && html.endsWith('-->');
      return !noContent;
    }
    return false;
  }

  getFormattedMessage(message: string): string {
    // Return the original message without any formatting
    return message || '';
  }

  private formatStructuredContent(text: string): string {
    let formatted = text;

    // Format main headers (Day 1, Day 2, etc.)
    formatted = formatted.replace(
      /(Day\s+\d+[:\s]*[^<]*)/gi,
      '<br><br><h3 style="color: #2563eb; font-size: 1.25rem; font-weight: bold; margin: 1rem 0 0.5rem 0; padding: 0.5rem 0; border-bottom: 2px solid #e5e7eb;">$1</h3>',
    );

    // Format time sections (Morning, Afternoon, Evening, etc.)
    formatted = formatted.replace(
      /(Morning|Afternoon|Evening|Night)[:\s]*/gi,
      '<br><h4 style="color: #374151; font-size: 1.1rem; font-weight: 600; margin: 0.75rem 0 0.25rem 0; padding-left: 0.5rem; border-left: 3px solid #3b82f6;">$1:</h4>',
    );

    // Format section headers (Dining Options:, Bars:, etc.)
    formatted = formatted.replace(
      /(Dining Options?|Bars?|Restaurants?|Hotels?|Attractions?|Events?|Places to Visit|Things to Do|Where to Eat|Where to Stay):/gi,
      '<br><br><h4 style="color: #1f2937; font-size: 1.1rem; font-weight: 600; margin: 1rem 0 0.5rem 0; padding: 0.5rem; background-color: #f3f4f6; border-radius: 0.375rem;">$1:</h4>',
    );

    // Format numbered lists (1. 2. 3. etc.) with better styling
    formatted = formatted.replace(
      /(\d+\.\s+[^<\n]+)/g,
      '<div style="margin: 0.5rem 0; padding: 0.5rem; background-color: #f9fafb; border-left: 3px solid #3b82f6; border-radius: 0 0.375rem 0.375rem 0;"><strong style="color: #1f2937;">$1</strong></div>',
    );

    // Format bullet points with better styling
    formatted = formatted.replace(
      /([•·▪▫-])\s+([^<\n]+)/g,
      '<div style="margin: 0.25rem 0; padding-left: 1rem; position: relative;"><span style="position: absolute; left: 0; color: #6b7280;">•</span><span style="color: #374151;">$2</span></div>',
    );

    // Format place names and establishments with enhanced styling
    formatted = formatted.replace(
      /([A-Z][a-zA-Z\s&'.-]+(?:Restaurant|Bar|Cafe|Grill|Tavern|Brewing|Company|Co\.|Inc\.|LLC|Park|Beach|Museum|Gallery|Theater|Hotel|Inn|Lodge|Resort|Center|Bureau|Agency|Shop|Store|Market|Plaza|Mall|Square|Garden|Trail|Path|Walk|Drive|Avenue|Street|Road|Boulevard|Way|Lane|Court|Place|Parkway|Highway|Blvd|Ave|St|Rd|Dr|Way|Ln|Ct|Pl|Pkwy|Hwy))\s*[-–—]\s*/g,
      '<div style="margin: 0.75rem 0; padding: 0.75rem; background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%); border-radius: 0.5rem; border: 1px solid #e2e8f0;"><strong style="color: #1e40af; font-size: 1.05rem;">$1</strong> - ',
    );

    // Format ratings and contact info
    formatted = formatted.replace(
      /(\d+\.\d+)\s+(Beach|Park|Restaurant|Hotel|Cafe|Bar|Shop|Museum|Gallery|Theater|Agency|Bureau|Center|Company|Co\.|Inc\.|LLC)/gi,
      '<span style="background-color: #fef3c7; color: #92400e; padding: 0.125rem 0.375rem; border-radius: 0.25rem; font-size: 0.875rem; font-weight: 600; margin-right: 0.5rem;">⭐ $1 $2</span>',
    );

    // Format phone numbers
    formatted = formatted.replace(
      /(\(\d{3}\)\s*\d{3}-\d{4})/g,
      '<span style="background-color: #dbeafe; color: #1e40af; padding: 0.125rem 0.375rem; border-radius: 0.25rem; font-size: 0.875rem; font-weight: 500; margin: 0 0.25rem;">📞 $1</span>',
    );

    // Format addresses
    formatted = formatted.replace(
      /([A-Za-z\s]+(?:Blvd|Ave|St|Rd|Dr|Way|Ln|Ct|Pl|Pkwy|Hwy|Boulevard|Avenue|Street|Road|Drive|Way|Lane|Court|Place|Parkway|Highway)[^,]*,\s*[^,]+,\s*[A-Z]{2}\s*\d{5})/g,
      '<span style="background-color: #f0f9ff; color: #0369a1; padding: 0.125rem 0.375rem; border-radius: 0.25rem; font-size: 0.875rem; margin: 0 0.25rem;">📍 $1</span>',
    );

    // Format business types/categories
    formatted = formatted.replace(
      /(Coffee Shop|Deli|Tea House|Cafe|Food|Wholesaler|Tourist Information Center|Visitor's Bureau|Natural Feature|Tourist Attraction|Tour Agency|Travel Agency|Restaurant|American Restaurant|Food|Coffee Shop|Deli|Tea House|Cafe|Wholesaler|Tourist Information Center|Visitor's Bureau)/gi,
      '<span style="background-color: #f3e8ff; color: #7c3aed; padding: 0.125rem 0.375rem; border-radius: 0.25rem; font-size: 0.75rem; margin: 0 0.125rem; display: inline-block;">$1</span>',
    );

    // Format line breaks for better readability
    formatted = formatted.replace(/\n/g, '<br>');

    // Clean up repetitive text - generic pattern for any location
    formatted = formatted.replace(
      /(\b\w+\s+)+(\b\w+\s+)(Beach|City|Town|Park|Lake|River|Mountain|Island)\b/gi,
      (match) => {
        // Remove duplicate words in location names
        const words = match.trim().split(/\s+/);
        const uniqueWords = [];
        for (let i = 0; i < words.length; i++) {
          if (i === 0 || words[i].toLowerCase() !== words[i - 1].toLowerCase()) {
            uniqueWords.push(words[i]);
          }
        }
        return uniqueWords.join(' ');
      },
    );

    // Add proper spacing around paragraphs
    formatted = formatted.replace(/(<br>){2,}/g, '<br><br>');

    // Wrap the entire content in a container for better styling
    formatted = `<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #374151;">${formatted}</div>`;

    return formatted;
  }

  private removeUnwantedUrls(text: string): string {
    // Only remove specific unwanted URL patterns, not all URLs
    const unwantedPatterns = [
      /https:\/\/maps\.google\.com\/[^\s]+/g,
      /http:\/\/www\.[^\/]+\.[^\/]+/g,
    ];

    let cleaned = text;
    unwantedPatterns.forEach((pattern) => {
      cleaned = cleaned.replace(pattern, '');
    });

    return cleaned;
  }

  private removeLocationInfo(text: string): string {
    // Remove common location patterns
    const patterns = [
      // Remove Google Maps URLs
      /https:\/\/maps\.google\.com\/[^\s]+/g,
      // Remove address patterns like "Torre Blvd, New Smyrna Beach"
      /[A-Za-z\s]+(?:Blvd|Ave|St|Rd|Dr|Way|Ln|Ct|Pl|Pkwy|Hwy|Boulevard|Avenue|Street|Road|Drive|Way|Lane|Court|Place|Parkway|Highway)[^,]*,\s*[^,]+/g,
      // Remove state and zip patterns like "FL, USA , FL 32168, USA"
      /[A-Z]{2},\s*USA[^,]*,\s*[A-Z]{2}\s*\d{5},\s*USA/g,
      // Remove city, state patterns like "New Smyrna Beach, FL, USA"
      /[A-Za-z\s]+,\s*[A-Z]{2},\s*USA/g,
      // Remove undefined values
      /\bundefined\b/g,
      // Remove locality/political info
      /\b(?:Locality|Political)\s+[^,]+/g,
      // Remove website URLs
      /https?:\/\/[^\s]+/g,
      // Remove specific location details
      /\b(?:Point Of Interest|Savannah Pointe)\s+\d+/g,
      // Remove city website patterns
      /http:\/\/www\.[^\/]+\.[^\/]+/g,
    ];

    patterns.forEach((pattern) => {
      text = text.replace(pattern, '');
    });

    return text;
  }

  private cleanAIResponse(text: string): string {
    // Clean up common AI response issues but preserve good structure
    let cleaned = text;

    // Only remove clearly unwanted phrases, not helpful content
    cleaned = cleaned.replace(
      /\bI'm sorry, but I don't have specific information about[^.]*\./g,
      '',
    );
    cleaned = cleaned.replace(/\bin my knowledge base\b/g, '');

    // Clean up extra commas and spaces but preserve structure
    cleaned = cleaned.replace(/,\s*,/g, ',');
    cleaned = cleaned.replace(/\s+/g, ' ');

    // Remove empty sentences
    cleaned = cleaned.replace(/\.\s*\./g, '.');
    cleaned = cleaned.replace(/,\s*$/g, '');

    // Clean up generic pattern for itinerary offers (works for any location)
    cleaned = cleaned.replace(
      /I'm sorry, but I don't have specific information about[^.]*\.\s*([^,]+),?\s+I can help you with a (\d+-day )?itinerary for ([^,]+),?\s*([^!]*)?if you're interested! Would you like that\?/gi,
      "I can help you with a $2itinerary for $3$4if you're interested! Would you like that?",
    );

    return cleaned.trim();
  }

  getTimeAgo(timestamp: Date): string {
    if (!timestamp) return '';

    const now = new Date();
    const messageTime = new Date(timestamp);
    const diffInMs = now.getTime() - messageTime.getTime();
    const diffInMinutes = Math.floor(diffInMs / 60000);
    const diffInHours = Math.floor(diffInMs / 3600000);
    const diffInDays = Math.floor(diffInMs / 86400000);

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} Min ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours} Hr ago`;
    } else {
      return `${diffInDays} Day${diffInDays > 1 ? 's' : ''} ago`;
    }
  }

  editMessage(): void {
    this.editMessageEvent.emit(this.message);
  }

  deleteMessage(): void {
    this.deleteMessageEvent.emit(this.message);
  }

  cleanQuestion(question: string): string {
    if (!question) return '';

    // Remove leading/trailing quotes and commas
    let cleaned = question.replace(/^[",\s]+|[",\s]+$/g, '');

    // Remove wrapping quotes if they exist
    cleaned = cleaned.replace(/^"(.*)"$/, '$1');

    // Remove escaped quotes
    cleaned = cleaned.replace(/\\"/g, '"');

    return cleaned.trim();
  }

  onSaveTrip(event: ButtonTriggerEvent): void {
    if (this.isSavingTrip) {
      this.snackBar.open('Saving trip, please wait...', 'OK', {
        horizontalPosition: 'center',
        verticalPosition: 'top',
        duration: 2000,
      });
      return;
    }
    this.isSavingTrip = true;

    if (this.tripTitle.trim()) {
      this.saveTripEvent.emit({
        message: this.message,
        title: this.tripTitle,
        callback: () => {
          this.tripTitle = '';
          this.saveTripMenuTrigger?.closeMenu();
          this.isSavingTrip = false;
          event?.immediateComplete();
        },
      });
    }
  }

  onSaveTripMenuClick(event: MouseEvent): void {
    if (this.isAllowClick) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.requestLoginEvent.emit();
  }
}
